import { Button, Flex, Result, Space, Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ErrorFallbackProps {
  error: Error | unknown;
  resetError: () => void;
  /**
   * Reporter reference for this crash (e.g. a Sentry event id), returned
   * by the `onError` the consuming app supplied. Shown so a reader can
   * quote it to support.
   */
  eventId?: string;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  eventId,
}) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const [showDetails, setShowDetails] = React.useState(false);

  const details = error instanceof Error ? error.message : String(error);

  const emailSupport = () => {
    const reference = eventId ? ` (#${eventId})` : '';
    const subject = `Error report${reference}`;
    // origin + pathname, never `href`: the media-player experience route
    // carries a `?access_token=` preview token, and a plaintext email is
    // the last place it should end up.
    const { origin, pathname } = globalThis.location;
    const body = `Error at ${origin}${pathname}\n\nDetails: ${details}`;
    globalThis.open(
      `mailto:support@libertify.com?subject=${encodeURIComponent(
        subject,
      )}&body=${encodeURIComponent(body)}`,
    );
  };

  return (
    <Result
      status="error"
      title={t('experienceV2.pageError.title')}
      // Deliberately NOT `error.message`: that is developer text, and
      // putting it here is how a reader ended up reading
      // `can't access property "legacy", globalThis.pdfJs is undefined`.
      // It stays available behind the toggle below, for support.
      subTitle={t('experienceV2.pageError.body')}
      extra={
        <Flex vertical align="center" gap={12}>
          <Space size="large">
            <Button shape="round" onClick={emailSupport}>
              {t('experienceV2.pageError.contactSupport')}
            </Button>
            <Button shape="round" type="primary" onClick={resetError}>
              {t('experienceV2.pageError.reload')}
            </Button>
          </Space>
          {eventId ? (
            <Typography.Text type="secondary" copyable={{ text: eventId }}>
              {t('experienceV2.pageError.reference', { id: eventId })}
            </Typography.Text>
          ) : null}
          {showDetails ? (
            <Typography.Text code>{details}</Typography.Text>
          ) : (
            <Button
              type="text"
              size="small"
              onClick={() => setShowDetails(true)}
            >
              {t('experienceV2.pageError.showDetails')}
            </Button>
          )}
        </Flex>
      }
    />
  );
};

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  /**
   * Report the crash to the consuming app's error reporter. Return a
   * reference (e.g. a Sentry event id) to show it to the reader. This lib
   * carries no Sentry dependency of its own — admin is on `@sentry/react`,
   * media-player on `@sentry/nextjs`, and sales has no Sentry at all.
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => string | undefined;
}

interface InnerProps extends ErrorBoundaryProps {
  fallback: (props: {
    error: Error;
    resetError: () => void;
    eventId?: string;
  }) => React.ReactNode;
}

class ErrorBoundaryInner extends React.Component<
  InnerProps,
  { error: Error | null; eventId?: string }
> {
  state: { error: Error | null; eventId?: string } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    const eventId = this.props.onError?.(error, errorInfo);
    if (eventId) this.setState({ eventId });
  }

  resetError = () => {
    this.setState({ error: null, eventId: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.error) {
      return this.props.fallback({
        error: this.state.error,
        resetError: this.resetError,
        eventId: this.state.eventId,
      });
    }
    return this.props.children;
  }
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  onReset,
  onError,
  children,
}) => {
  return (
    <ErrorBoundaryInner
      onReset={onReset ?? (() => globalThis.location.reload())}
      onError={onError}
      fallback={({ error, resetError, eventId }) => (
        <ErrorFallback
          error={error}
          resetError={resetError}
          eventId={eventId}
        />
      )}
    >
      {children}
    </ErrorBoundaryInner>
  );
};

export default ErrorBoundary;
